import { Component, type ReactNode } from 'react';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { SkillConfirmation } from '../SkillConfirmation/SkillConfirmation';
import { isSkillDataValid } from '../../utils/conversation';
import type { ConversationMessage, PageSkill } from '../../types';
import { useChatbotSession } from '../../providers/ChatbotProvider';

class SkillErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const SkillStatus = ({ text }: { text: string }) => (
  <div role="status">
    <Copy size="m" text={text} />
  </div>
);

export const SkillMessage = ({ message, skill }: { message: ConversationMessage; skill?: PageSkill }) => {
  const { t } = useChatbotSession();

  if (!message.skillName || message.skillStatus === 'complete') return null;
  if (message.skillStatus === 'invalid') return <SkillStatus text={t('chatbot.skillInvalid')} />;
  if (message.skillStatus === 'failed') return <SkillStatus text={t('chatbot.skillFailed')} />;
  if (message.skillStatus === 'executing') return <SkillStatus text={t('chatbot.skillExecuting')} />;
  if (message.skillStatus === 'declined') return <SkillStatus text={t('chatbot.skillDeclined')} />;
  if (message.skillStatus === 'awaiting-confirmation' && skill) {
    return <SkillConfirmation message={message} skill={skill} />;
  }
  if (message.skillStatus === 'component' && skill?.execute) return null;
  if (message.skillStatus !== 'component' || !skill?.component) return <SkillStatus text={t('chatbot.skillUnavailable')} />;
  if (!isSkillDataValid(skill, message)) return <SkillStatus text={t('chatbot.skillInvalid')} />;

  const SkillComponent = skill.component;

  return (
    <SkillErrorBoundary key={message.id} fallback={<SkillStatus text={t('chatbot.skillFailed')} />}>
      <SkillComponent skillData={message.skillData ?? {}} />
    </SkillErrorBoundary>
  );
};
